import { action, Action, createTransform, persist } from "easy-peasy";
import { DocumentNode } from "graphql";
import { Query, QueryVerticalTalentSearchArgs } from "src/graphql/types";
import { graphQLClient } from "src/graphql/client";
import { isKnownVertical } from "src/utils/user";

export interface CachedQuery {
  query: DocumentNode;
  variables: QueryVerticalTalentSearchArgs;
  data: Query;
}

type QueryCache = "createJob" | "editJob";
interface SetCachedQueryPayload {
  context: QueryCache;
  query: CachedQuery;
}

export interface QueryCacheModel {
  setCachedQuery: Action<QueryCacheModel, SetCachedQueryPayload>;
  evictCachedQuery: Action<QueryCacheModel, QueryCache>;
  createJob: CachedQuery | null;
  editJob: CachedQuery | null;
}

// A search cached for a vertical this build does not know is dropped whole:
// its results answer that vertical, so they cannot stand in for a narrower
// search.
const knownSearch = (cached: CachedQuery | null) => {
  const { vertical, verticals } = cached?.variables || {};
  const named = [...(vertical ? [vertical] : []), ...(verticals || [])];
  return named.every(isKnownVertical) ? cached : null;
};

const dropUnknownVerticals = createTransform(undefined, (data, key) =>
  key === "createJob" || key === "editJob" ? knownSearch(data) : data,
);

const persistInLocalStorage = (model: QueryCacheModel) =>
  persist(model, {
    storage: "localStorage",
    transformers: [dropUnknownVerticals],
  });

export const queryCache: QueryCacheModel = persistInLocalStorage({
  setCachedQuery: action((state, { context, query }) => {
    state[context] = query;
  }),
  evictCachedQuery: action((state, context) => {
    graphQLClient.cache.evict({
      id: "ROOT_QUERY",
      fieldName: "verticalTalentSearch",
    });
    state[context] = null;
  }),
  createJob: null,
  editJob: null,
});
